import AgencyNavbar from "@/components/agency/AgencyNavbar";
import AgencyFooter from "@/components/agency/AgencyFooter";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Search, MapPin, BarChart3, TrendingUp, Globe, CheckCircle2, FileText, Code2, Calendar } from "lucide-react";
import seoHero from "@/assets/agency/local-seo-website.jpg";

const whatIDo = [
  { icon: Search, title: "Keyword Research", desc: "We find the exact phrases your customers search — 'roofer near me,' 'Auburn plumber,' 'kitchen remodel Kent.' Then we build your site around them." },
  { icon: Code2, title: "On-Page SEO", desc: "Every page gets optimized title tags, meta descriptions, header structure, image alt text, and internal linking — built right into the site." },
  { icon: MapPin, title: "Local SEO Setup", desc: "Google Business optimization, schema markup, NAP consistency, and city-specific landing pages so you show up in your service areas." },
  { icon: FileText, title: "Content Strategy", desc: "We write SEO-optimized content that answers what homeowners actually search. Blog posts, service pages, FAQ sections — all included." },
];

const howItHelps = [
  "Homeowners search 'roofer near me' — your site shows up first",
  "Google Business listing appears in the map pack for your city",
  "Your site loads fast on mobile — Google rewards that with higher rankings",
  "Service area pages rank individually for each city you serve",
  "Schema markup helps Google understand your business and display rich results",
  "Blog content builds authority and captures long-tail search traffic",
];

const faqs = [
  { q: "Is SEO really included in the website price?", a: "Yes. Every site we build includes on-page SEO, schema markup, Google Business setup, and local optimization. There's no separate SEO charge — it's baked into the design from day one." },
  { q: "How long does it take to see SEO results?", a: "Most contractors see meaningful ranking improvements within 3-4 months. Full results typically take 6-12 months, but you'll start appearing in local searches well before that." },
  { q: "Do I need a blog for SEO?", a: "Not required, but it helps. Blog posts target specific search queries that your main pages can't. We include blog setup in Pro and Premium packages." },
  { q: "What about Google Ads vs SEO?", a: "Google Ads gets instant traffic but stops when you stop paying. SEO builds lasting visibility. Most contractors benefit from both, but SEO is the foundation. We focus on organic search — if you need ads, we can recommend a specialist." },
  { q: "Will you manage my Google Business profile?", a: "We set it up and optimize it as part of your website project. For ongoing management (posting updates, responding to reviews), that's something we can discuss as an add-on." },
  { q: "How much does SEO cost for contractors?", a: "Most agencies charge $1,000 to $3,000 a month for contractor SEO on top of a website build. Here the SEO is part of the build, and ongoing local SEO starts at $400 a month on the Essential plan, $750 on Growth and $1,150 on the Website Growth Partner plan. No setup surprises and no long contracts." },
  { q: "How many location pages should a contractor website have?", a: "One page for each city you genuinely serve and can describe in real detail, usually five to fifteen. A page needs its own local content, jobs and service notes. Ten thin copy-paste city pages do more harm than five good ones." },
  { q: "Do contractors really need SEO?", a: "If you rely on referrals only, you are invisible to the homeowner who opens Google at 9pm with a leaking roof. Local search is where most emergency and replacement work starts, and unlike ads it keeps working after you stop paying." },
];

const guideLinks = [
  { to: "/blog/roofing-contractor-seo-complete-2027-strategy", label: "Roofing contractor SEO: the complete strategy" },
  { to: "/blog/seattle-contractors-location-specific-pages", label: "Why Seattle contractors need location-specific pages" },
  { to: "/blog/plumbing-seo-2028-whats-changed", label: "Plumbing SEO: what has changed and what still works" },
  { to: "/blog/gutter-installation-seo-pacific-northwest", label: "Ranking a gutter business in the Pacific Northwest" },
  { to: "/blog/contractor-website-cost-2028", label: "What a contractor website should actually cost" },
  { to: "/king-county-contractors", label: "Contractor SEO by city across King County" },
];

const tradeLinks = [
  { to: "/contractor-websites/roofing-contractors", label: "Roofing website design and SEO" },
  { to: "/contractor-websites/hvac-contractors", label: "HVAC website design and SEO" },
  { to: "/contractor-websites/plumbing-contractors", label: "Plumber website design and SEO" },
  { to: "/contractor-websites/general-contracting-contractors", label: "General contractor SEO" },
  { to: "/contractor-websites/electrical-contractors", label: "Electrician website design and SEO" },
  { to: "/contractor-websites/remodeling-contractors", label: "Remodeling website design and SEO" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const ContractorSEO = () => {
  return (
    <div className="min-h-screen bg-agency-bg font-sans text-agency-text">
      <SEOHead
        title="Contractor SEO Seattle | Rank for Local Jobs | jaik"
        description="SEO for Seattle contractors: roofing, plumbing, remodeling. Get top Google rankings in King County. Included with every website build."
        canonical="https://jaikmarketing.com/contractor-seo"
        ogImage="/og-contractor-seo.jpg"
        jsonLd={jsonLd}
      />
      <AgencyNavbar />

      <main>
        {/* Hero */}
        <section className="relative min-h-[650px] sm:min-h-[700px] flex items-end overflow-hidden">
          <img src={seoHero} alt="Contractor checking a call log on his phone in a work truck with the Seattle skyline behind" width={1536} height={960} className="absolute inset-0 h-full w-full object-cover object-[68%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-agency-bg via-agency-bg/90 to-agency-bg/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-agency-bg via-transparent to-agency-bg/30" />
          <div className="container relative z-10 pb-14 pt-32 sm:pb-20 sm:pt-40">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
              <span className="font-sans text-xs text-agency-accent tracking-[0.2em] uppercase mb-4 block">SEO for Contractors</span>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                How SEO Gets Contractors More Jobs in King County
              </h1>
              <p className="font-sans text-agency-text-muted text-lg sm:text-xl leading-relaxed mb-8">
                 Show up when local homeowners search for your trade. Every site includes the technical and local SEO foundation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book" className="btn-jaik btn-jaik-primary btn-jaik-lg">
                  <Calendar className="h-4 w-4" /> Book a Strategy Call
                </Link>
                <Link to="/pricing" className="btn-jaik btn-jaik-secondary btn-jaik-lg">
                  See packages and pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* What is Local SEO */}
        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-6">
              What is Local SEO?
            </h2>
            <div className="space-y-4 font-sans text-agency-text-muted text-base leading-relaxed">
              <p>
                When a homeowner in Seattle needs a roofer, they open Google and type "roofing contractor near me." Local SEO is what determines whether <strong className="text-agency-text">your</strong> business shows up — or your competitor's.
              </p>
              <p>
                It's a combination of how your website is structured, what content it has, how fast it loads, whether Google understands your business, and how consistent your information is across the internet.
              </p>
              <p>
                The good news: <strong className="text-agency-text">We build all of this into every contractor website from day one.</strong> You don't need to hire a separate SEO agency or pay monthly retainers just to get found on Google.
              </p>
            </div>
          </div>
        </section>

        {/* What I Do for SEO */}
        <section className="py-16 sm:py-24">
          <div className="container">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-12">
              What I Do for Your SEO
            </h2>
            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
              {whatIDo.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-agency-surface border border-agency-border rounded-xl p-6"
                >
                  <div className="w-10 h-10 rounded-lg bg-agency-accent/10 border border-agency-accent/20 flex items-center justify-center mb-4">
                    <item.icon className="h-5 w-5 text-agency-accent" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="font-sans text-sm text-agency-text-muted leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Helps */}
        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-8">
              How This Helps Your Business
            </h2>
            <div className="space-y-3">
              {howItHelps.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3 bg-agency-surface border border-agency-border rounded-lg px-5 py-4"
                >
                  <CheckCircle2 className="h-5 w-5 text-agency-accent flex-shrink-0 mt-0.5" />
                  <span className="font-sans text-sm text-agency-text">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24">
          <div className="container max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-10">
              SEO Questions Contractors Ask
            </h2>
            <div className="space-y-6">
              {faqs.map((f, i) => (
                <motion.div
                  key={f.q}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-agency-surface border border-agency-border rounded-xl p-6"
                >
                  <h3 className="font-heading text-base font-semibold mb-2">{f.q}</h3>
                  <p className="font-sans text-sm text-agency-text-muted leading-relaxed">{f.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO by trade */}
        <section className="py-16 sm:py-24">
          <div className="container max-w-4xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">
              SEO built for your trade
            </h2>
            <p className="font-sans text-agency-text-muted mb-8 max-w-2xl leading-relaxed">
              A roofer and a plumber need very different things from a website. Roofing buyers compare bids for weeks, plumbing buyers call in under a minute. Here is how I build for each one.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {tradeLinks.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="flex items-center justify-between gap-4 bg-agency-surface border border-agency-border rounded-lg px-5 py-4 hover:border-agency-accent/40 transition-colors"
                >
                  <span className="font-sans text-sm text-agency-text">{t.label}</span>
                  <ArrowRight className="h-4 w-4 text-agency-accent flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Guides */}
        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-8">
              Go deeper on contractor SEO
            </h2>
            <ul className="space-y-3">
              {guideLinks.map((g) => (
                <li key={g.to}>
                  <Link
                    to={g.to}
                    className="flex items-center justify-between gap-4 bg-agency-surface border border-agency-border rounded-lg px-5 py-4 hover:border-agency-accent/40 transition-colors"
                  >
                    <span className="font-sans text-sm text-agency-text">{g.label}</span>
                    <ArrowRight className="h-4 w-4 text-agency-accent flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-agency-accent/8 via-agency-surface/30 to-agency-bg" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-agency-accent/5 rounded-full blur-[100px]" />
          <div className="container relative z-10 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-6">
              SEO Is Included — Let's Get You Ranked
            </h2>
            <p className="font-sans text-agency-text-muted text-lg mb-8 max-w-xl mx-auto">
              Every website we build for contractors includes local SEO from the start. Ready to get found on Google?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book" className="btn-jaik btn-jaik-primary btn-jaik-lg">
                <Calendar className="h-4 w-4" /> Book a Strategy Call
              </Link>
              <Link to="/pricing" className="btn-jaik btn-jaik-secondary btn-jaik-lg">
                See packages and pricing <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <AgencyFooter />
    </div>
  );
};

export default ContractorSEO;
